import React from "react";

import PageHeader from "@replo/design-system/blocks/page-header/PageHeader";
import { ListItem } from "@replo/design-system/components/list-item/ListItem";
import { toast } from "@replo/design-system/components/toast/Toast";
import twMerge from "@replo/design-system/utils/twMerge";
import { useCurrentProjectContext } from "@v2/contexts/CurrentProjectContext";
import { useCurrentWorkspaceId } from "@v2/contexts/WorkspaceDashboardContext";
import { isFeatureEnabled } from "@v2/features/infra/featureFlags";
import useCurrentProjectId from "@v2/hooks/useCurrentProjectId";
import { getProjectName } from "@v2/utils/project-utils";
import { routes } from "@v2/utils/router";
import {
  ALargeSmall,
  Code2,
  Puzzle,
  Receipt,
  Settings2,
  TestTubeDiagonal,
  Users,
} from "lucide-react";
import {
  generatePath,
  Outlet,
  useLocation,
  useNavigate,
} from "react-router-dom";
import { exhaustiveSwitch } from "replo-utils/lib/misc";

const baseShopSidebarItems = [
  {
    label: "General",
    path: routes.shops.settings.shops.general,
    icon: Settings2,
  },
  {
    label: "Fonts",
    path: routes.shops.settings.shops.fonts,
    icon: ALargeSmall,
  },
  {
    label: "A/B Testing",
    path: routes.shops.settings.shops.abtesting,
    icon: TestTubeDiagonal,
  },
];

const scriptManagerItem = {
  label: "Script Manager",
  path: routes.shops.settings.shops.scripts,
  icon: Code2,
};

const integrationsItem = {
  label: "Integrations",
  path: routes.shops.settings.shops.integrations.home,
  icon: Puzzle,
};

const getShopSidebarItems = ({
  isReploHostedProject,
  shouldShowLegacyIntegrationsItem,
}: {
  isReploHostedProject: boolean;
  shouldShowLegacyIntegrationsItem: boolean;
}) => {
  return [
    ...baseShopSidebarItems,
    ...(isReploHostedProject ? [scriptManagerItem] : []),
    ...(shouldShowLegacyIntegrationsItem ? [integrationsItem] : []),
  ];
};

const workspaceSidebarItems = [
  {
    label: "General",
    path: routes.shops.settings.workspace.general,
    icon: Settings2,
  },
  {
    label: "Members",
    path: routes.shops.settings.workspace.members,
    icon: Users,
  },
  {
    label: "Billing",
    path: routes.shops.settings.workspace.billing,
    icon: Receipt,
  },
];

const workspaceOnlySidebarItems = [
  {
    label: "General",
    path: routes.workspace.settings.general,
    icon: Settings2,
  },
  {
    label: "Members",
    path: routes.workspace.settings.members,
    icon: Users,
  },
  {
    label: "Billing",
    path: routes.workspace.settings.billing,
    icon: Receipt,
  },
];

const isItemSelected = (path: string) => {
  return location.pathname.startsWith(path);
};

function resolveSettingsPath({
  path,
  params,
}: {
  path: string;
  params: Record<string, string | null | undefined>;
}): string | null {
  if (Object.values(params).some((v) => v == null)) {
    return null;
  }
  return generatePath(path, params as Record<string, string>);
}

type SidebarItem = {
  label: string;
  path: string;
  icon: React.ElementType;
};

const IconContainer = ({ children }: React.PropsWithChildren) => (
  <div className="flex h-5 w-5 items-center justify-center rounded-sm bg-surface">
    {children}
  </div>
);

const SettingsSidebarItem = ({
  item,
  params,
}: {
  item: SidebarItem;
  params: Record<string, string | null | undefined>;
}) => {
  const navigate = useNavigate();
  const resolvedPath = resolveSettingsPath({ path: item.path, params });

  return (
    <ListItem
      render={<button type="button" />}
      startEnhancer={
        <IconContainer>
          <item.icon size={12} />
        </IconContainer>
      }
      key={item.path}
      onClick={() => {
        if (resolvedPath) {
          navigate(resolvedPath);
        }
      }}
      className="h-8"
      selected={resolvedPath ? isItemSelected(resolvedPath) : false}
      label={item.label}
    />
  );
};

const ShopSettings = () => {
  const navigate = useNavigate();
  const projectId = useCurrentProjectId();
  const workspaceId = useCurrentWorkspaceId();
  const location = useLocation();
  const { userRole, isLoading, project } = useCurrentProjectContext();
  const shouldShowNewIntegrationsUi = isFeatureEnabled("integrations-v2-ui");

  // Check if we're in workspace-only mode (no project context)
  const isWorkspaceOnly = !project && !projectId;

  // Only check access after loading is complete
  const canViewWorkspace = React.useMemo(() => {
    // If still loading, don't make access decisions yet
    if (isLoading) {
      return undefined;
    }

    return exhaustiveSwitch({ type: userRole ?? "none" })({
      superuser: () => true,
      workspaceOwner: () => true,
      workspaceMember: () => true,
      projectMember: () => false,
      none: () => false,
    });
  }, [userRole, isLoading]);

  // Handle redirect for unauthorized access to workspace settings
  React.useEffect(() => {
    // Only redirect if we're not loading and the user doesn't have access
    if (
      !isLoading &&
      canViewWorkspace === false &&
      location.pathname.includes("/settings/workspace")
    ) {
      toast.error({
        title: "You do not have access to this workspace.",
        description: "Please contact the workspace admin to gain access.",
      });

      navigate(
        generatePath(routes.shops.settings.shops.general, {
          projectId,
        }),
        {
          replace: true,
        },
      );
    }
  }, [canViewWorkspace, isLoading, location.pathname, projectId, navigate]);

  // Choose the appropriate sidebar items based on context
  const workspaceItems = isWorkspaceOnly
    ? workspaceOnlySidebarItems
    : workspaceSidebarItems;

  return (
    <div className="flex flex-col w-full overflow-hidden">
      <PageHeader title="Settings" />
      <div className="flex pt-6 pb-6 w-full overflow-hidden">
        <div className="w-[160px] space-y-2">
          {/* Only show Shop section when not in workspace-only mode */}
          {!isWorkspaceOnly && (
            <>
              <div className="header-h6">{getProjectName(project)}</div>
              <div
                className={twMerge(
                  "border-b-0.5 border-border pb-2 flex flex-col gap-1",
                  !canViewWorkspace && "border-b-0 pb-0",
                )}
              >
                {getShopSidebarItems({
                  isReploHostedProject: project?.hostingType === "replo",
                  shouldShowLegacyIntegrationsItem:
                    !shouldShowNewIntegrationsUi,
                }).map((item) => (
                  <SettingsSidebarItem
                    key={item.path}
                    item={item}
                    params={{ projectId }}
                  />
                ))}
              </div>
            </>
          )}
          {/* Show workspace section when we have access or in workspace-only mode */}
          {/* Don't show workspace section while loading unless in workspace-only mode */}
          {((!isLoading && canViewWorkspace) || isWorkspaceOnly) && (
            <>
              <div className="header-h6">Workspace</div>
              <div className="flex flex-col gap-1">
                {workspaceItems.map((item) => (
                  <SettingsSidebarItem
                    key={item.path}
                    item={item}
                    params={isWorkspaceOnly ? { workspaceId } : { projectId }}
                  />
                ))}
              </div>
            </>
          )}
        </div>
        <div className="flex-1 px-6 overflow-y-auto justify-center flex h-full items-start">
          <Outlet />
        </div>
      </div>
    </div>
  );
};

export default ShopSettings;
